import api from "./api";

// Portfolio files (photos, audio, PDFs) may be up to 40 MB. Videos keep their
// own larger limits and always use the chunked upload.
export const MAX_PORTFOLIO_FILE_BYTES = 40 * 1024 * 1024;
export const MAX_PORTFOLIO_FILE_LABEL = "40 MB";

// Files at or below this size still use the single-request base64 upload.
// Anything larger goes up in 4 MB chunks so it never hits the nginx body
// limit or MongoDB's 16 MB document limit.
const INLINE_UPLOAD_BYTES = 12 * 1024 * 1024;

// The server stores photos at max 2000 px anyway, so shrinking large photos in
// the browser first loses nothing and keeps the upload small.
const IMAGE_MAX_SIDE = 2000;
const IMAGE_RESIZE_ABOVE_BYTES = 4 * 1024 * 1024;

export const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(file);
});

export const isResizableImage = (file) =>
  (file?.type || "").startsWith("image/") && !["image/gif", "image/svg+xml"].includes(file.type);

/** Shrink a large photo to max 2000 px JPEG. Returns the original file when
 *  it is already small, not a resizable image, or the browser can't decode it. */
export async function prepareImageForUpload(file) {
  if (!isResizableImage(file) || file.size <= IMAGE_RESIZE_ABOVE_BYTES) return file;
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const scale = Math.min(1, IMAGE_MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const context = canvas.getContext("2d");
    context.fillStyle = "#fff"; // transparent PNGs flatten on white, as on the server
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
    if (!blob) return file;
    const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], name, { type: "image/jpeg" });
  } catch (error) {
    console.warn("Could not shrink image before upload; sending the original.", error);
    return file;
  }
}

/** Upload a file in chunks through /media/video/* (videos, and audio/PDFs over 12 MB). */
export async function uploadInChunks(file, { type = "gallery", title, onProgress, posterDataUrl, durationSeconds, retry = (fn) => fn() } = {}) {
  const start = await api.post("/media/video/start", {
    filename: file.name, mime: file.type || "application/octet-stream",
    size: file.size, type, title: title || file.name,
    ...(durationSeconds ? { duration_seconds: durationSeconds } : {}),
  });
  const sessionId = start.data.session_id;
  const chunkSize = start.data.chunk_size || (4 * 1024 * 1024);
  const totalChunks = Math.ceil(file.size / chunkSize);
  for (let index = 0; index < totalChunks; index += 1) {
    const slice = file.slice(index * chunkSize, (index + 1) * chunkSize);
    await retry(() => {
      const form = new FormData();
      form.append("session_id", sessionId);
      form.append("chunk_index", String(index));
      form.append("chunk", slice, `${file.name}.part${index}`);
      return api.post("/media/video/chunk", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (event) => {
          const chunkProgress = event.total ? event.loaded / event.total : 0;
          onProgress?.((index + chunkProgress) / totalChunks);
        },
      });
    });
  }
  const finish = new FormData();
  finish.append("session_id", sessionId);
  if (posterDataUrl) finish.append("poster_data_url", posterDataUrl);
  const result = await retry(() => api.post("/media/video/finish", finish, {
    headers: { "Content-Type": "multipart/form-data" },
  }));
  return result.data;
}

/** Upload one non-video portfolio file (photo, audio or PDF) up to 40 MB. */
export async function uploadPortfolioFile(file, { type = "gallery", onProgress, retry } = {}) {
  if (file.size > MAX_PORTFOLIO_FILE_BYTES) {
    throw new Error(`${file.name} is too large (max ${MAX_PORTFOLIO_FILE_LABEL})`);
  }
  const prepared = await prepareImageForUpload(file);
  if (prepared.size > INLINE_UPLOAD_BYTES) {
    return uploadInChunks(prepared, { type, title: file.name, onProgress, retry });
  }
  const dataUrl = await fileToDataUrl(prepared);
  const result = await api.post("/media/upload", { type, data_url: dataUrl, title: file.name }, {
    onUploadProgress: (event) => onProgress?.(event.total ? event.loaded / event.total : 0),
  });
  return result.data;
}
